import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ProtectedRoute from '../ProtectedRoute';
import { Spinner } from '@/components/ui/spinner';
import CreateCtaTopBar from '../presentation/screens/create/reusable/components/CreateCtaTopBar';
import CreateEngTopBar from '../presentation/screens/create/reusable/components/CreateEngTopBar';
import EndScreenTopbar from '../presentation/screens/create/reusable/components/EndScreenTopbar';
import { useSelector } from 'react-redux';
import AgentTopBar from '../presentation/agents/create/reusable/components/AgentTopBar';
import CreateCollectionTopBarV1 from '../presentation/screens/create/reusable/components/CreateCollectionTopBarV1';
import { path } from 'slate';
import { compose } from '@reduxjs/toolkit';
import { selectIsEventsPlatform } from '../redux/slices/platform/platformSlice';

/** Preserve query/hash when redirecting legacy collections-v1 URLs to recirculations. */
const LegacyCollectionsV1Redirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// Use React.lazy() to lazily load the components
const LazyNotFound = lazy(() => import('../pages/NotFound'));
// Component showcase (/theme): development builds only, so production never bundles it.
const LazyTheme =
  process.env.NODE_ENV !== 'production' ? lazy(() => import('../pages/Theme')) : null;
const LazyLogin = lazy(() => import('../pages/authentication/Login'));
const LazyRegister = lazy(() => import('../pages/authentication/Register'));
const LazyConfirmEmail = lazy(() => import('../pages/authentication/ConfirmEmail'));
const LazyForgotPassword = lazy(() => import('../pages/authentication/ForgotPassword'));
const LazyDashboard = lazy(() => import('../pages/Dashboard'));
const LazyCTADashboard = lazy(() => import('../pages/screens/dashboards/CTAsDashboards'));
const LazyCTACreate = lazy(() => import('../pages/screens/create/CreateCTA'));
const LazyCTAEdit = lazy(() => import('../pages/screens/edit/EditCTA'));
const LazyEngagementDashboard = lazy(
  () => import('../pages/screens/dashboards/EngagementDashboards')
);
const LazyEngCreate = lazy(() => import('../pages/screens/create/CreateEng'));
const LazyEngEdit = lazy(() => import('../pages/screens/edit/EditEng'));
const LazyEngDraft = lazy(() => import('../pages/screens/draft/EngDraft'));
const LazyAIEngagementDashboard = lazy(
  () => import('../pages/screens/dashboards/AIEngagementDashboards')
);
const LazyCTADraft = lazy(() => import('../pages/screens/draft/CTADraft'));
const LazyEndScreenDashboard = lazy(
  () => import('../pages/screens/dashboards/EndScreenDashboards')
);
const LazyEndScreenCreate = lazy(() => import('../pages/screens/create/CreateEndScreen'));
const LazyEndScreenEdit = lazy(() => import('../pages/screens/edit/EditEndScreen'));
const LazyEndScreenDraft = lazy(() => import('../pages/screens/draft/EndScreenDraft'));
const LazyScreensDraftDashboard = lazy(
  () => import('../pages/screens/dashboards/ScreensDraftDashboard')
);
const LazyDomains = lazy(() => import('../pages/domains/Domains'));
const LazyPages = lazy(() => import('../pages/domains/Pages'));
const LazyProfile = lazy(() => import('../pages/setting/Profile'));
const LazyIntegrations = lazy(() => import('../pages/setting/Integrations'));
const LazyTeam = lazy(() => import('../pages/setting/Team'));
const LazySecurity = lazy(() => import('../pages/setting/Security'));
const LazyPerformance = lazy(() => import('../pages/analytics/Performance'));
const LazyVideos = lazy(() => import('../pages/setting/Videos'));
const LazyCreditUsage = lazy(() => import('../pages/setting/CreditUsage'));
const LazyUsers = lazy(() => import('../pages/analytics/Users'));
const LazyLeads = lazy(() => import('../pages/leads/Leads'));
const LazyAgentDashboard = lazy(() => import('../pages/agents/dashboard/agents/AgentDashboard'));
const LazyCreateAgent = lazy(() => import('../pages/agents/create/CreateAgent'));
const LazyAgentDraftDashboard = lazy(
  () => import('../pages/agents/dashboard/draft/AgentDraftDashboard')
);
const LazyAgentsDraft = lazy(() => import('../pages/agents/draft/AgentsDraft'));
const LazyAgentOverview = lazy(() => import('../pages/agents/overview/AgentOverview'));
const LazyEditAgent = lazy(() => import('../pages/agents/edit/EditAgent'));
const LazyCollectionsDashboardV1 = lazy(
  () => import('../pages/screens/dashboards/CollectionsDashboardV1')
);
const LazyCollectionsDashboardV2 = lazy(
  () => import('../pages/screens/dashboards/CollectionsDashboardV2')
);
const LazyCollectionsOverviewV1 = lazy(
  () => import('../pages/screens/overview/CollectionsOverviewV1')
);
const LazyCreateCollectionV1 = lazy(() => import('../pages/screens/create/CreateCollectionV1'));
const LazyCreateCollectionV2 = lazy(() => import('../pages/screens/create/CreateCollectionV2'));
const LazyAILabsDashboard = lazy(() => import('../pages/AILabs/dashboard/AILabsDashboard'));
const LazyCreateEngine = lazy(() => import('../pages/AILabs/create/CreateEngine'));
const LazyEditEngine = lazy(() => import('../pages/AILabs/edit/EditEngine'));

const LazyFineTuningDashboard = lazy(
  () => import('../pages/FineTuning/dashboard/FineTuningDashboard')
);

const LazyABTestWorkflow = lazy(() => import('../pages/ABTest/ABTestWorkflow'));
const LazyABTestOverview = lazy(() => import('../pages/ABTest/ABTestOverview'));

const LazySponsors = lazy(() => import('../pages/Sponsors/Sponsors'));
const LazySponsorsOverview = lazy(() => import('../pages/Sponsors/SponsorsOverview'));
const LazyEvents = lazy(() => import('../pages/events/Events'));
const LazyEventDetail = lazy(() => import('../pages/events/EventDetail'));
const LazyEventWorkflows = lazy(() => import('../pages/events/EventWorkflows'));
const LazyEventBranding = lazy(() => import('../pages/events/EventBranding'));
const LazyEventLeads = lazy(() => import('../pages/events/EventLeads'));
const LazyEventWorkflowAgents = lazy(() => import('../pages/events/EventWorkflowAgents'));

function AppRouter() {
  const accessibleSection = useSelector(state => state.team.data.accessibleSection);
  const hasCreditAccess = useSelector(state => state.app.hasCreditAccess);
  const isSponsorsActive = useSelector(state => state.branding.branding.isSponsorsActive);
  const isEventsPlatform = useSelector(selectIsEventsPlatform);

  // Function to handle page type and layout
  const handlePageTypeAndLayout = (Children, isProtected, HeaderComp) => {
    return (
      <ProtectedRoute isProtected={isProtected} HeaderComp={HeaderComp}>
        <Suspense fallback={fallback}>
          <Children />
        </Suspense>
      </ProtectedRoute>
    );
  };

  const fallback = (
    <div className="flex h-screen w-full flex-wrap items-center justify-center">
      <Spinner className="size-9 text-primary" />
    </div>
  );

  const routes = [
    { path: '/login', component: LazyLogin, isProtected: false },
    { path: '/register', component: LazyRegister, isProtected: false },
    {
      path: '/confirmemail/:email',
      component: LazyConfirmEmail,
      isProtected: false,
    },
    {
      path: '/forgotpassword',
      component: LazyForgotPassword,
      isProtected: false,
    },
    {
      path: '/',
      component: LazyDashboard,
      isProtected: true,
      sectionname: 'dashboard',
    },
    {
      path: '/engagement/dashboard',
      component: LazyEngagementDashboard,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/engagement/create',
      component: LazyEngCreate,
      isProtected: true,
      headerComponent: CreateEngTopBar,
      sectionname: 'screens',
    },
    {
      path: '/engagement/edit',
      component: LazyEngEdit,
      isProtected: true,
      headerComponent: CreateEngTopBar,
      sectionname: 'screens',
    },
    {
      path: '/engagement/draft',
      component: LazyEngDraft,
      isProtected: true,
      headerComponent: CreateEngTopBar,
      sectionname: 'screens',
    },
    // {
    //   path: '/aiengagement/dashboard',
    //   component: LazyAIEngagementDashboard,
    //   isProtected: true,
    //   sectionname: 'screens',
    // },
    {
      path: '/cta/dashboard',
      component: LazyCTADashboard,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/cta/create',
      component: LazyCTACreate,
      isProtected: true,
      headerComponent: CreateCtaTopBar,
      sectionname: 'screens',
    },
    {
      path: '/cta/edit',
      component: LazyCTAEdit,
      isProtected: true,
      headerComponent: CreateCtaTopBar,
      sectionname: 'screens',
    },
    {
      path: '/cta/draft',
      component: LazyCTADraft,
      isProtected: true,
      headerComponent: CreateCtaTopBar,
      sectionname: 'screens',
    },
    {
      path: '/endscreen/dashboard',
      component: LazyEndScreenDashboard,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/endscreen/create',
      component: LazyEndScreenCreate,
      isProtected: true,
      headerComponent: EndScreenTopbar,
      sectionname: 'screens',
    },
    {
      path: '/endscreen/edit',
      component: LazyEndScreenEdit,
      isProtected: true,
      headerComponent: EndScreenTopbar,
      sectionname: 'screens',
    },
    {
      path: '/endscreen/draft',
      component: LazyEndScreenDraft,
      isProtected: true,
      headerComponent: EndScreenTopbar,
      sectionname: 'screens',
    },
    {
      path: '/recirculations/dashboard',
      component: LazyCollectionsDashboardV1,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/recirculations/overview',
      component: LazyCollectionsOverviewV1,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/recirculations/create',
      component: LazyCreateCollectionV1,
      isProtected: true,
      headerComponent: CreateCollectionTopBarV1,
      sectionname: 'screens',
    },
    {
      path: '/collections/dashboard',
      component: LazyCollectionsDashboardV2,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/collections/labels',
      component: LazyCollectionsDashboardV2,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/collections/create',
      component: LazyCreateCollectionV2,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/screens-draft',
      component: LazyScreensDraftDashboard,
      isProtected: true,
      sectionname: 'screens',
    },
    {
      path: '/domains',
      component: LazyDomains,
      isProtected: true,
      sectionname: 'website',
    },
    // {
    //   path: '/Pages',
    //   component: LazyPages,
    //   isProtected: true,
    //   sectionname: 'website',
    // },
    {
      path: '/performance',
      component: LazyPerformance,
      isProtected: true,
      sectionname: 'analytics',
    },
    {
      path: '/users',
      component: LazyUsers,
      isProtected: true,
      sectionname: 'analytics',
    },
    {
      path: '/leads',
      component: LazyLeads,
      isProtected: true,
      sectionname: 'leads',
    },
    {
      path: '/agents/dashboard',
      component: LazyAgentDashboard,
      isProtected: true,
      sectionname: 'campaigns',
    },
    {
      path: '/agents/create',
      component: LazyCreateAgent,
      isProtected: true,
      headerComponent: AgentTopBar,
      sectionname: 'campaigns',
    },
    {
      path: '/agents/edit',
      component: LazyEditAgent,
      isProtected: true,
      headerComponent: AgentTopBar,
      sectionname: 'campaigns',
    },
    {
      path: '/agents/draft',
      component: LazyAgentsDraft,
      isProtected: true,
      headerComponent: AgentTopBar,
      sectionname: 'campaigns',
    },
    {
      path: '/agents-draft',
      component: LazyAgentDraftDashboard,
      isProtected: true,
      sectionname: 'campaigns',
    },
    {
      path: '/agents/overview',
      component: LazyAgentOverview,
      isProtected: true,
      sectionname: 'campaigns',
    },
    {
      path: '/aiLabs/dashboard',
      component: LazyAILabsDashboard,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    {
      path: '/aiLabs/create',
      component: LazyCreateEngine,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    {
      path: '/aiLabs/edit',
      component: LazyEditEngine,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    {
      path: '/fine-tuning/dashboard',
      component: LazyFineTuningDashboard,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    {
      path: '/abTest',
      component: LazyABTestWorkflow,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    {
      path: '/abTest/overview',
      component: LazyABTestOverview,
      isProtected: true,
      sectionname: 'aiLabs',
    },
    ...(isSponsorsActive
      ? [
          {
            path: '/sponsors',
            component: LazySponsors,
            isProtected: true,
            sectionname: 'aiLabs',
          },
          {
            path: '/sponsors/overview',
            component: LazySponsorsOverview,
            isProtected: true,
            sectionname: 'aiLabs',
          },
        ]
      : []),
    ...(isEventsPlatform
      ? [
          {
            path: '/events',
            component: LazyEvents,
            isProtected: true,
            sectionname: 'campaigns',
          },
        ]
      : []),
  ];
  let constantRoutes = [
    { path: '/profile', component: LazyProfile, isProtected: true },
    { path: '/integrations', component: LazyIntegrations, isProtected: true },
    { path: '/team', component: LazyTeam, isProtected: true },
    { path: '/security', component: LazySecurity, isProtected: true },
    { path: '/videos', component: LazyVideos, isProtected: true },
  ];

  if (hasCreditAccess) {
    constantRoutes.push({ path: '/credit-usage', component: LazyCreditUsage, isProtected: true });
  }

  let filteredRoutes;
  if (accessibleSection?.length === 0) {
    filteredRoutes = routes;
  } else {
    filteredRoutes = routes.filter(item => accessibleSection.includes(item.sectionname));
  }

  const hasCampaignsAccess =
    accessibleSection?.length === 0 || accessibleSection.includes('campaigns');
  const showEventDetailRoutes = isEventsPlatform && hasCampaignsAccess;

  return (
    <Router>
      <Routes>
        {[...filteredRoutes, ...constantRoutes].map(
          ({ path, component, isProtected, headerComponent }) => (
            <Route
              key={path}
              path={path}
              element={handlePageTypeAndLayout(component, isProtected, headerComponent)}
            />
          )
        )}

        {showEventDetailRoutes && (
          <Route path="/events/:eventId" element={handlePageTypeAndLayout(LazyEventDetail, true)}>
            <Route index element={<Navigate to="workflows" replace />} />
            <Route
              path="workflows"
              element={
                <Suspense fallback={fallback}>
                  <LazyEventWorkflows />
                </Suspense>
              }
            />
            <Route
              path="workflows/:workflowKey"
              element={
                <Suspense fallback={fallback}>
                  <LazyEventWorkflowAgents />
                </Suspense>
              }
            />
            <Route
              path="branding"
              element={
                <Suspense fallback={fallback}>
                  <LazyEventBranding />
                </Suspense>
              }
            />
            <Route
              path="leads"
              element={
                <Suspense fallback={fallback}>
                  <LazyEventLeads />
                </Suspense>
              }
            />
          </Route>
        )}

        {/* Legacy collections-v1 → recirculations */}
        <Route
          path="/collections-v1/dashboard"
          element={<LegacyCollectionsV1Redirect to="/recirculations/dashboard" />}
        />
        <Route
          path="/collections-v1/overview"
          element={<LegacyCollectionsV1Redirect to="/recirculations/overview" />}
        />
        <Route
          path="/collections-v1/create"
          element={<LegacyCollectionsV1Redirect to="/recirculations/create" />}
        />

        {LazyTheme && (
          <Route
            path="/theme"
            element={
              <Suspense fallback={fallback}>
                <LazyTheme />
              </Suspense>
            }
          />
        )}

        <Route key="*" path="*" element={handlePageTypeAndLayout(LazyNotFound, true)} />
      </Routes>
    </Router>
  );
}

export default AppRouter;
